import type { ConnectorType } from '@/types/connectors';
import type { ConnectorTypeSchemaResponse } from '@/types/connector-schema';
import { buildZodSchema } from '@/lib/validation/buildConnectorSchema';
import { getVisibleFieldNames } from '@/lib/validation/fieldVisibility';
import { validateConnectorName, CONNECTOR_NAME_ERROR } from '@/lib/validation/connectorName';
import type { ConnectorConfigFormData } from '../ConfigureConnector';
import type { WizardStepKey } from './wizardSteps';

export interface WizardValidationResult {
  isValid: boolean;
  errors: Partial<Record<keyof ConnectorConfigFormData, string>>;
}

export const validateFormData = (
  connectorType: ConnectorType | null,
  formData: ConnectorConfigFormData,
  stepKey: WizardStepKey | undefined,
  schema?: ConnectorTypeSchemaResponse | null
): WizardValidationResult => {
  const errors: Partial<Record<keyof ConnectorConfigFormData, string>> = {};

  if (stepKey === 'type') {
    return { isValid: connectorType !== null, errors };
  }

  if (stepKey === 'configure') {
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Connector name is required';
    } else if (!validateConnectorName(formData.name)) {
      // Validate the raw name (not trimmed) to match the backend, which
      // rejects e.g. a leading space — so a name that passes here is never
      // rejected for format at final submit.
      errors.name = CONNECTOR_NAME_ERROR;
    }

    if (schema) {
      const visibleFields = getVisibleFieldNames(schema.fields, formData as Record<string, unknown>);
      const zodSchema = buildZodSchema(schema.fields, visibleFields);
      const result = zodSchema.safeParse(formData);
      if (!result.success) {
        for (const issue of result.error.issues) {
          const fieldName = issue.path[0] as keyof ConnectorConfigFormData;
          if (!errors[fieldName]) {
            errors[fieldName] = issue.message;
          }
        }
      }
    }
  }

  // Scheduling is collected on the dedicated Schedule step (#273). The UI keeps
  // cron/enabled in sync, so this is a safety net against an enabled schedule
  // with no expression.
  if (stepKey === 'schedule') {
    if (formData.schedule_enabled && !formData.schedule_cron) {
      errors.schedule_cron = 'Schedule expression is required when scheduling is enabled';
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};
